// =========================================================================
// GOOGLE PLAY BILLING PRODUCTS (real money) - Post Boost + Green Tick.
// Shared by the app (lib/play-billing.ts) and the server (app/api/billing/verify).
//
// !! The `productId` values below MUST be identical to the Product IDs you created in
// !! Play Console (Monetize -> Products -> In-app products / Subscriptions). Edit them here if yours differ.
//
// Prices are NOT stored here: they live in Play Console (per country) and the app shows the localized price
// that Google Play returns. The server never trusts a price from the phone - it only trusts what Google says.
// =========================================================================

export const PLAY_PACKAGE_NAME_DEFAULT = 'com.animora.social'

export type PlayProduct =
  | { productId: string; type: 'inapp'; kind: 'boost'; hours: number; label: string }
  | { productId: string; type: 'inapp'; kind: 'username'; chars: number; label: string }
  | { productId: string; type: 'inapp'; kind: 'coins'; coins: number; bonus: number; label: string; badge?: string }
  | { productId: string; type: 'subs'; kind: 'green_tick'; days: number; label: string; basePlanId: string }

// Boost = one-time CONSUMABLE in-app products (can be bought again and again).
export const PLAY_BOOST_PRODUCTS: PlayProduct[] = [
  { productId: 'boost_6h', type: 'inapp', kind: 'boost', hours: 6, label: '6 Hours' },
  { productId: 'boost_24h', type: 'inapp', kind: 'boost', hours: 24, label: '24 Hours' },
  { productId: 'boost_3d', type: 'inapp', kind: 'boost', hours: 72, label: '3 Days' },
]

// Premium username (1-4 characters) = one-time CONSUMABLE in-app products, one per length. Bought once; the handle is
// then owned until the person changes it (it is released and has to be bought again to get a short one back).
export const PLAY_USERNAME_PRODUCTS: PlayProduct[] = [
  { productId: 'username_1', type: 'inapp', kind: 'username', chars: 1, label: '1-Letter Username' },
  { productId: 'username_2', type: 'inapp', kind: 'username', chars: 2, label: '2-Letter Username' },
  { productId: 'username_3', type: 'inapp', kind: 'username', chars: 3, label: '3-Letter Username' },
  { productId: 'username_4', type: 'inapp', kind: 'username', chars: 4, label: '4-Letter Username' },
]

// COIN STORE (like Free Fire diamonds) = one-time CONSUMABLE in-app products. `coins` = what the pack is called, `bonus` = extra free
// coins on the bigger packs. The person gets coins + bonus. Prices are set ONLY in Play Console (per country) - never here.
// Suggested India prices: 100 = Rs 80, 250 = Rs 200, 500 = Rs 380, 1000 = Rs 750, 2500 = Rs 1800, 5000 = Rs 3500.
export const PLAY_COIN_PRODUCTS: PlayProduct[] = [
  { productId: 'coins_100', type: 'inapp', kind: 'coins', coins: 100, bonus: 0, label: '100 Coins' },
  { productId: 'coins_250', type: 'inapp', kind: 'coins', coins: 250, bonus: 10, label: '250 Coins' },
  { productId: 'coins_500', type: 'inapp', kind: 'coins', coins: 500, bonus: 35, label: '500 Coins', badge: 'Popular' },
  { productId: 'coins_1000', type: 'inapp', kind: 'coins', coins: 1000, bonus: 100, label: '1000 Coins' },
  { productId: 'coins_2500', type: 'inapp', kind: 'coins', coins: 2500, bonus: 300, label: '2500 Coins', badge: 'Best value' },
  { productId: 'coins__5000', type: 'inapp', kind: 'coins', coins: 5000, bonus: 750, label: '5000 Coins' },
]

// Green Tick = auto-renewing monthly SUBSCRIPTION (Google renews it; the server re-checks it every hour).
export const PLAY_GREEN_TICK_PRODUCT: PlayProduct = {
  productId: 'green_tick', type: 'subs', kind: 'green_tick', days: 30, label: 'Green Tick - 1 Month', basePlanId: 'monthly', // base plan ID from Play Console
}

export const PLAY_PRODUCTS: PlayProduct[] = [...PLAY_BOOST_PRODUCTS, ...PLAY_USERNAME_PRODUCTS, ...PLAY_COIN_PRODUCTS, PLAY_GREEN_TICK_PRODUCT]

export function findPlayProduct(productId: string): PlayProduct | undefined {
  return PLAY_PRODUCTS.find((p) => p.productId === productId)
}

/** BOOST_PLANS id ('boost_6h' ...) -> its Play product (same ids by default). */
export function playProductForBoostPlan(planId: string): PlayProduct | undefined {
  return PLAY_BOOST_PRODUCTS.find((p) => p.productId === planId)
}

/** Play product for a premium handle of this many characters (1-4), or undefined. */
export function playProductForUsernameLength(chars: number): Extract<PlayProduct, { kind: 'username' }> | undefined {
  return PLAY_USERNAME_PRODUCTS.find((p) => p.kind === 'username' && p.chars === chars) as any
}
